// Light, dark, or the device's setting: one choice per device, kept in localStorage and applied as
// `data-theme` on <html> (tokens.css follows it; without it, the device's prefers-color-scheme).
// Apps call applyTheme() before their first render; maps and anything else drawn in colour listen
// for "pmu-theme" to redraw.
import { useEffect, useState } from "react";
import { Icon } from "./icons.tsx";
import { IconButton } from "./components/Button.tsx";
import { cx } from "./presentation.tsx";

export type ThemePref = "system" | "light" | "dark";
const KEY = "pmu.theme";

export function getThemePref(): ThemePref {
  try { const v = localStorage.getItem(KEY); return v === "light" || v === "dark" ? v : "system"; } catch { return "system"; }
}

/** The theme in effect now: the choice, or the device's when the choice is "system". */
export function effectiveTheme(pref = getThemePref()): "light" | "dark" {
  if (pref !== "system") return pref;
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function applyTheme(pref = getThemePref()) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (pref === "system") delete root.dataset.theme; else root.dataset.theme = pref;
  root.style.colorScheme = effectiveTheme(pref);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", effectiveTheme(pref) === "dark" ? "#12100e" : "#f8f6f3");
  window.dispatchEvent(new CustomEvent("pmu-theme", { detail: effectiveTheme(pref) }));
}

export function setThemePref(pref: ThemePref) {
  try { if (pref === "system") localStorage.removeItem(KEY); else localStorage.setItem(KEY, pref); } catch { /* per device, best effort */ }
  applyTheme(pref);
}

// The device switching between light and dark while the choice is "system".
if (typeof matchMedia !== "undefined") matchMedia("(prefers-color-scheme: dark)").addEventListener?.("change", () => { if (getThemePref() === "system") applyTheme("system"); });

export function useThemePref(): [ThemePref, (p: ThemePref) => void] {
  const [pref, setPref] = useState<ThemePref>(getThemePref);
  useEffect(() => { const on = () => setPref(getThemePref()); window.addEventListener("pmu-theme", on); return () => window.removeEventListener("pmu-theme", on); }, []);
  return [pref, (p) => { setThemePref(p); setPref(p); }];
}

const CHOICES: { value: ThemePref; label: string; icon: "sun" | "moon" | "phone" }[] = [
  { value: "light", label: "Light", icon: "sun" }, { value: "dark", label: "Dark", icon: "moon" }, { value: "system", label: "Device", icon: "phone" },
];

/** Three buttons: Light, Dark, Device (follow the phone or computer). */
export function ThemeChoice({ className }: { className?: string }) {
  const [pref, setPref] = useThemePref();
  return (
    <div role="radiogroup" aria-label="Appearance" className={cx("pmu-themechoice", className)}>
      {CHOICES.map((c) => (
        <button key={c.value} type="button" role="radio" aria-checked={pref === c.value} className="pmu-themechoice__opt" onClick={() => setPref(c.value)}>
          <Icon name={c.icon} /><span>{c.label}</span>
        </button>
      ))}
    </div>
  );
}

/** One button that steps Light → Dark → Device (the console's top bar). */
export function ThemeToggle() {
  const [pref, setPref] = useThemePref();
  const i = CHOICES.findIndex((c) => c.value === pref);
  const cur = CHOICES[i]!, next = CHOICES[(i + 1) % CHOICES.length]!;
  return <IconButton icon={cur.icon} label={`Appearance: ${cur.label}. Switch to ${next.label}`} onClick={() => setPref(next.value)} />;
}
